import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNotifications } from '@decisiv/ui-components';
import Rocket from '@decisiv/iconix/lib/components/Rocket';
import noop from 'lodash/noop';
import { t, Trans } from '@lingui/macro';

// Vite fingerprints the entry bundle on every build and rewrites the
// module <script> in index.html to point at it (e.g.
// `<script type="module" crossorigin src="/assets/index-abc123.js">`).
// That src is our version signal: when it changes, a new UI was deployed.
// Parse rather than regex-match so attribute order does not matter.
function findEntrySrc(htmlStr) {
  return new DOMParser()
    .parseFromString(htmlStr, 'text/html')
    .querySelector('script[type="module"][src]')
    ?.getAttribute('src');
}

export default function AutoReload({ url, tryDelay }) {
  const [previousSrc, setPreviousSrc] = useState();
  const { notify } = useNotifications();

  function reloadApp(e) {
    window.location.reload(true);
    e.preventDefault();
  }

  const fetchSource = useCallback(() => {
    async function fetchData() {
      const ms = Date.now();
      const response = await fetch(`${url}?w=${ms}`, {
        headers: {
          'cache-control': 'no-store',
          pragma: 'no-cache',
        },
      });

      if (response.status !== 200) {
        throw new Error('offline');
      }

      const html = await response.text();
      const src = findEntrySrc(html);
      if (!src) {
        return;
      }
      if (!previousSrc) {
        setPreviousSrc(src);
        return;
      }
      if (previousSrc !== src) {
        notify(
          {
            title: t`New version available`,
            intent: 'information',
            icon: Rocket,
            onClose: noop,
            actions: [
              {
                text: t`RELOAD TO UPDATE NOW`,
                onClick: reloadApp,
              },
            ],
          },
          <Trans>
            Update to the latest enhancements and feature improvements. It only
            takes a few seconds.
          </Trans>,
        );
        setPreviousSrc(src);
      }
    }

    // Polling failures (offline, transient 5xx) are expected; swallow them so
    // they do not surface as unhandled rejections.
    fetchData().catch(noop);
  }, [notify, previousSrc, url]);

  // Seed the baseline with the entry bundle the page actually loaded, so a
  // release that ships between page load and the first poll is still caught.
  useEffect(() => {
    const src = document
      .querySelector('script[type="module"][src]')
      ?.getAttribute('src');
    if (src) {
      setPreviousSrc(src);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchSource, tryDelay);

    return () => clearInterval(interval);
  }, [fetchSource, tryDelay]);
  return null;
}

AutoReload.propTypes = {
  url: PropTypes.string,
  tryDelay: PropTypes.number,
};

AutoReload.defaultProps = {
  url: '/index.html',
  tryDelay: 5 * 60 * 1000, // 5 minutes
};
